import React from 'react'
import { IoFlameOutline, IoArrowUpOutline, IoArrowDownOutline } from 'react-icons/io5'
import { Widget } from '../Widget'
import { useTrendingTeams } from '../../../swr-config'
import type { PageId } from '../../BottomNav'

interface TrendingTeam {
  team_id: number
  name: string
  crest_url?: string | null
  trend_score: number
  trend_direction: 'up' | 'down' | 'stable'
  reason: string
  form: string
}

interface TrendingTeamsWidgetProps {
  isEditing?: boolean
  onRemove?: () => void
  onNavigate?: (page: PageId) => void
  maxItems?: number
}

export function TrendingTeamsWidget({
  isEditing,
  onRemove,
  onNavigate,
  maxItems = 6,
}: TrendingTeamsWidgetProps) {
  const { data: teams, error, isLoading } = useTrendingTeams(maxItems)

  const handleTeamClick = (team: TrendingTeam) => {
    // Use pushState for better history control, then dispatch hashchange
    window.history.pushState(null, '', `#team/${team.team_id}`)
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  }

  const getTrendIcon = (direction: string) => {
    switch (direction) {
      case 'up':
        return <IoArrowUpOutline size={14} className="trend-icon trend-icon--up" />
      case 'down':
        return <IoArrowDownOutline size={14} className="trend-icon trend-icon--down" />
      default:
        return null
    }
  }

  return (
    <Widget
      id="trending"
      title="Trending Teams"
      icon={<IoFlameOutline size={18} />}
      isEditing={isEditing}
      onRemove={onRemove}
      onNavigate={onNavigate}
      isLoading={isLoading}
      error={error ? 'Failed to load trending teams' : null}
      skeletonVariant="trending"
    >
      <div className="widget-trending">
        {!teams || teams.length === 0 ? (
          <div className="widget-empty">
            <p>No trending teams available</p>
          </div>
        ) : (
          <div className="widget-trending-grid">
            {teams.map((team) => (
              <button
                key={team.team_id}
                className="widget-trending-item"
                onClick={() => handleTeamClick(team)}
                title={team.reason}
              >
                <div className="widget-trending-header">
                  {team.crest_url ? (
                    <img
                      src={team.crest_url}
                      alt={team.name}
                      className="widget-trending-crest"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="widget-trending-crest-placeholder" />
                  )}
                  {getTrendIcon(team.trend_direction)}
                </div>
                <span className="widget-trending-name">{team.name}</span>
                <span className="widget-trending-form">
                  {team.form.split('').map((result, i) => (
                    <span
                      key={i}
                      className={`form-dot form-dot--${result.toLowerCase()}`}
                    />
                  ))}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Widget>
  )
}
